'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface LikeButtonProps {
  postId: string;
}

export default function LikeButton({ postId }: LikeButtonProps) {
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLikes = useCallback(async () => {
    try {
      const response = await fetch(`/api/blog/${postId}/likes`);
      
      if (!response.ok) {
        console.error('Failed to fetch likes:', response.status);
        return;
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Invalid content type received:', contentType);
        return;
      }

      const data = await response.json();
      setLikes(data.totalLikes || 0);
      setHasLiked(data.hasLiked || false);
    } catch (error) {
      console.error('Error fetching likes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchLikes();
  }, [fetchLikes]);

  const handleLike = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`/api/blog/${postId}/likes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Invalid content type received');
      }

      const data = await response.json();

      if (response.ok) {
        setLikes(data.totalLikes);
        setHasLiked(data.hasLiked);
        
        toast({
          title: data.hasLiked ? "Gefällt mir!" : "Like entfernt",
          description: data.hasLiked 
            ? "Ihr Like wurde hinzugefügt." 
            : "Ihr Like wurde entfernt.",
        });
      } else {
        console.error('Like API error:', data);
        throw new Error(data.error || data.details || 'Failed to like post');
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Like konnte nicht verarbeitet werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={handleLike}
      disabled={isLoading}
      className={`transition-all duration-200 ${
        hasLiked 
          ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100' 
          : 'hover:bg-gray-50'
      }`}
    >
      <Heart 
        className={`w-4 h-4 mr-2 transition-all duration-200 ${
          hasLiked ? 'fill-red-500 text-red-500' : 'text-gray-500'
        }`} 
      />
      {hasLiked ? 'Gefällt mir' : 'Gefällt mir'} 
      {likes > 0 && (
        <span className="ml-2 text-sm font-medium">
          {likes}
        </span>
      )}
    </Button>
  );
} 
